import React, { useState } from "react";
import {
  useGetAdminDocumentTypesQuery,
  usePostAdminDocumentTypeMutation,
  usePutAdminDocumentTypeMutation,
  useDeleteAdminDocumentTypeMutation,
} from "@/store/adminService";
import ApplicantDocumentType from "@/model/interface/ApplicantDocumentType";
import ApplicantDocumentTypeField from "@/model/interface/ApplicantDocumentTypeField";
import { Pencil, Trash2, Plus, FileType } from "lucide-react";

const emptyField: ApplicantDocumentTypeField = {
  name: "",
  type: "text",
  label: "",
  description: "",
  validation: "",
};

const emptyForm = {
  name: "",
  type: "",
  description: "",
  pointsValue: 0,
  fields: [{ ...emptyField }],
};

const inputClass =
  "w-full h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0]";

export default function AdminDocumentTypes() {
  const { data: documentTypes, isLoading } = useGetAdminDocumentTypesQuery();
  const [postDocumentType] = usePostAdminDocumentTypeMutation();
  const [putDocumentType] = usePutAdminDocumentTypeMutation();
  const [deleteDocumentType] = useDeleteAdminDocumentTypeMutation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const typeAlreadyExists =
    form.type.trim() !== "" &&
    documentTypes?.some((existing) => existing.type === form.type && existing.id !== editingId);

  const openAddForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm, fields: [{ ...emptyField }] });
    setShowForm(true);
  };

  const openEditForm = (dt: ApplicantDocumentType) => {
    setEditingId(dt.id);
    setForm({
      name: dt.name,
      type: dt.type,
      description: dt.description,
      pointsValue: dt.pointsValue,
      fields: dt.fields.length > 0 ? dt.fields.map((f) => ({ ...f })) : [{ ...emptyField }],
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm, fields: [{ ...emptyField }] });
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      fields: form.fields.filter((f) => f.name.trim() !== "" || f.label.trim() !== ""),
    };

    if (editingId) {
      await putDocumentType({ id: editingId, ...payload });
    } else {
      await postDocumentType(payload);
    }
    cancelForm();
  };

  const handleDelete = async (id: string) => {
    await deleteDocumentType(id);
    setConfirmDeleteId(null);
  };

  const updateField = (index: number, key: keyof ApplicantDocumentTypeField, value: string) => {
    setForm((prev) => {
      const fields = [...prev.fields];
      fields[index] = { ...fields[index], [key]: value };
      return { ...prev, fields };
    });
  };

  const addField = () => {
    setForm((prev) => ({ ...prev, fields: [...prev.fields, { ...emptyField }] }));
  };

  const removeField = (index: number) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading document types...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <FileType className="size-6" />
          Document Types
        </h2>
        {!showForm && (
          <button
            onClick={openAddForm}
            className="inline-flex items-center gap-1 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <Plus className="size-4" />
            Add Document Type
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? "Edit Document Type" : "Add Document Type"}
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className={inputClass}
                placeholder="e.g. Passport"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type / Slug</label>
              <input
                type="text"
                value={form.type}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, type: e.target.value.toLowerCase().replace(/\s+/g, "-") }))
                }
                className={`${inputClass} ${typeAlreadyExists ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
                placeholder="e.g. passport"
              />
              {typeAlreadyExists && <p className="text-xs text-red-500 mt-1">This type slug is already in use.</p>}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className={`${inputClass} h-auto py-3`}
              rows={3}
              placeholder="Describe this document type..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Points Value</label>
              <input
                type="number"
                value={form.pointsValue}
                onChange={(e) => setForm((prev) => ({ ...prev, pointsValue: Number(e.target.value) }))}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Fields</h4>
              <button
                onClick={addField}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <Plus className="size-3" />
                Add Field
              </button>
            </div>

            <div className="space-y-3">
              {form.fields.map((field, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-gray-500">Field {index + 1}</span>
                    {form.fields.length > 1 && (
                      <button
                        onClick={() => removeField(index)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <Trash2 className="size-3" />
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => updateField(index, "name", e.target.value)}
                        className={inputClass}
                        placeholder="field_name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select
                        value={field.type}
                        onChange={(e) => updateField(index, "type", e.target.value)}
                        className={inputClass}
                      >
                        <option value="text">Text</option>
                        <option value="date">Date</option>
                        <option value="month">Month</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(index, "label", e.target.value)}
                        className={inputClass}
                        placeholder="Display Label"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={field.description || ""}
                        onChange={(e) => updateField(index, "description", e.target.value)}
                        className={inputClass}
                        placeholder="Optional description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Validation</label>
                      <input
                        type="text"
                        value={field.validation || ""}
                        onChange={(e) => updateField(index, "validation", e.target.value)}
                        className={inputClass}
                        placeholder="Optional validation regex"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={typeAlreadyExists || !form.type.trim() || !form.name.trim()}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {editingId ? "Update" : "Save"}
            </button>
            <button
              onClick={cancelForm}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Fields</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(!documentTypes || documentTypes.length === 0) && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  No document types found.
                </td>
              </tr>
            )}
            {documentTypes?.map((dt) => (
              <tr key={dt.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-sm text-gray-900 font-medium">{dt.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {dt.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{dt.pointsValue}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{dt.fields?.length || 0}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditForm(dt)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer"
                    >
                      <Pencil className="size-3" />
                      Edit
                    </button>
                    {confirmDeleteId === dt.id ? (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(dt.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors cursor-pointer"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(dt.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
                      >
                        <Trash2 className="size-3" />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
